import { useToast } from "@ac-expo/ui-components";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type Props = {
  children: React.ReactNode;
};

type Coordinates = {
  latitude: number;
  longitude: number;
};

type UserLocationContextType = {
  location: Coordinates | null;
  isLoading: boolean;
};

const defaultValue: UserLocationContextType = {
  location: null,
  isLoading: true,
};

const UserGeolocationContext =
  createContext<UserLocationContextType>(defaultValue);

export const useUserGeolocation = () => {
  return useContext(UserGeolocationContext);
};

const GEOLOCATION_LOCAL_STORAGE_KEYS: Record<keyof Coordinates, string> = {
  latitude: "latitude",
  longitude: "longitude",
};

export const UserGeolocationProvider = ({ children }: Props) => {
  const { toast } = useToast();
  const [userGeolocation, setLocation] = useState<Coordinates | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const saveUserGeolocation = useCallback((position: GeolocationPosition) => {
    localStorage.setItem(
      GEOLOCATION_LOCAL_STORAGE_KEYS.latitude,
      position.coords.latitude.toString()
    );
    localStorage.setItem(
      GEOLOCATION_LOCAL_STORAGE_KEYS.longitude,
      position.coords.longitude.toString()
    );
    setLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    });
    setIsLoading(false);
  }, []);

  const getUserGeolocation = useCallback(() => {
    const latitude = localStorage.getItem(
      GEOLOCATION_LOCAL_STORAGE_KEYS.latitude
    );
    const longitude = localStorage.getItem(
      GEOLOCATION_LOCAL_STORAGE_KEYS.longitude
    );

    if (!latitude || !longitude) {
      return null;
    }

    return {
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
    };
  }, []);

  useEffect(() => {
    const storedUserGeolocation = getUserGeolocation();
    if (storedUserGeolocation !== null) {
      setLocation(storedUserGeolocation);
      setIsLoading(false);
      return;
    }

    if (navigator.geolocation) {
      const timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          saveUserGeolocation(position);
        },
        (error) => {
          clearTimeout(timeoutId);
          let message = error.message;
          if (error.code === error.PERMISSION_DENIED)
            message = "L'utilisateur a refusé la demande de permission";
          else if (error.code === error.POSITION_UNAVAILABLE)
            message = "Position non disponible";
          else if (error.code === error.TIMEOUT)
            message = "Le temps imparti pour obtenir la position a expiré";

          toast({
            title: "Erreur",
            description: `Impossible de trouver votre position. ${message}`,
          });
          setIsLoading(false);
        }
      );
    } else {
      setIsLoading(false);
    }
  }, [getUserGeolocation, saveUserGeolocation, setLocation, toast]);

  return (
    <UserGeolocationContext.Provider
      value={{ location: userGeolocation, isLoading }}
    >
      {children}
    </UserGeolocationContext.Provider>
  );
};
